import {
  Component,
  Input,
  OnInit,
  inject,
  signal,
  effect,
  untracked,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { PrivateFileUploadService } from '@rapider/services';

@Component({
  selector: 'rapider-application-artifacts',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex flex-col gap-4">
      <div
        class="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col gap-3"
      >
        <h4 class="text-sm font-semibold text-gray-700 m-0">
          Generated Artifacts
        </h4>
        <p class="text-xs text-gray-500 m-0">
          Files and outputs generated by this application's AI workflows.
        </p>

        @if (isLoading()) {
          <div class="flex items-center justify-center py-8">
            <i class="fas fa-circle-notch fa-spin text-2xl text-primary"></i>
          </div>
        } @else {
          <div class="flex flex-col gap-3 mt-2">
            @for (file of artifacts(); track file.id) {
              <div
                class="flex flex-col p-3 rounded-lg border border-gray-100 hover:border-primary/30 transition-colors group bg-gray-50/50"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2 overflow-hidden">
                    <i
                      class="fas fa-file-alt text-gray-400 text-lg shrink-0 group-hover:text-primary transition-colors"
                    ></i>
                    <span
                      class="text-sm font-medium text-gray-700 truncate"
                      [title]="file.name"
                      >{{ file.name }}</span
                    >
                  </div>
                  <button
                    (click)="downloadFile(file)"
                    class="shrink-0 w-8 h-8 rounded hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
                    title="Download File"
                  >
                    <i class="fas fa-download text-xs"></i>
                  </button>
                </div>
                <div
                  class="flex items-center gap-3 mt-2 text-[10px] text-gray-400 font-medium px-6"
                >
                  <span>{{ formatSize(file.size) }}</span>
                  @if (file.extension) {
                    <span class="uppercase">{{ file.extension }}</span>
                  }
                  <span>{{ file.createdAt | date: 'short' }}</span>
                </div>
              </div>
            }
            @if (artifacts().length === 0) {
              <div class="py-8 text-center flex flex-col items-center gap-2">
                <div
                  class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center"
                >
                  <i class="fas fa-folder-open text-gray-300 text-xl"></i>
                </div>
                <p class="text-sm text-gray-400 m-0">No artifacts found.</p>
                <p class="text-xs text-gray-400 m-0">
                  Run a workflow to generate outputs.
                </p>
              </div>
            }
          </div>
        }
      </div>
    </div>
  `,
})
export class ApplicationArtifactsComponent implements OnChanges {
  @Input() appDomain!: string;

  private readonly httpClient = inject(HttpClient);
  private readonly privateFileUploadService = inject(PrivateFileUploadService);

  public artifacts = signal<any[]>([]);
  public isLoading = signal<boolean>(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appDomain'] && this.appDomain) {
      this.fetchArtifacts();
    }
  }

  public fetchArtifacts(): void {
    if (!this.appDomain) return;

    this.isLoading.set(true);
    // Fetch folder with the exact name as domain
    const filter = encodeURIComponent(
      JSON.stringify({ where: { name: this.appDomain } }),
    );

    this.httpClient
      .get<any[]>(`/api/project-folders?filter=${filter}`)
      .subscribe({
        next: (folders) => {
          if (folders && folders.length > 0) {
            const folderId = folders[0].id;
            this.httpClient
              .get<any[]>(`/api/project-files/get-folder-files/${folderId}`)
              .subscribe({
                next: (files) => {
                  // Sort newest first
                  const sorted = (files || []).sort(
                    (a: any, b: any) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime(),
                  );
                  this.artifacts.set(sorted);
                  this.isLoading.set(false);
                },
                error: (err) => {
                  console.error(
                    '[ApplicationArtifacts] Failed to fetch folder files:',
                    err,
                  );
                  this.artifacts.set([]);
                  this.isLoading.set(false);
                },
              });
          } else {
            // Folder doesn't exist yet, so no artifacts
            this.artifacts.set([]);
            this.isLoading.set(false);
          }
        },
        error: (err) => {
          console.error(
            '[ApplicationArtifacts] Failed to fetch project folder:',
            err,
          );
          this.artifacts.set([]);
          this.isLoading.set(false);
        },
      });
  }

  public downloadFile(file: any): void {
    this.privateFileUploadService
      .getDownloadUrl(file.id)
      .then((url: string) => {
        // Create a temporary link element to trigger the download
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name || 'download';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((err: any) => {
        console.error('[ApplicationArtifacts] Failed to get download URL', err);
        // Fallback: alert the user
        alert(
          'Failed to generate download URL. You may not have access or the file expired.',
        );
      });
  }

  public formatSize(bytes?: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }
}
